import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  History as HistoryIcon,
  Receipt as ReceiptIcon,
  Build as BuildIcon,
  Pool as PoolIcon,
} from '@mui/icons-material';
import { usePortalAuth } from '../context/PortalAuthContext';
import { portalApi, ServiceHistoryItem, InvoiceItem, WorkOrderItem } from '../services/portalApi';
import { formatStatus } from '../utils/formatStatus';
import {
  dashboardActionAreaSx,
  dashboardListRowSx,
  dashboardStatCardSx,
} from '../styles/dashboardInteractions';
import { useTranslation } from 'react-i18next';
import { i18n } from '../i18n';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { customer, account } = usePortalAuth();
  const { t } = useTranslation();
  const [recentServices, setRecentServices] = useState<ServiceHistoryItem[]>([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState<InvoiceItem[]>([]);
  const [upcomingWorkOrders, setUpcomingWorkOrders] = useState<WorkOrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, invoicesRes, workOrdersRes] = await Promise.all([
          portalApi.getServiceHistory({ limit: 3 }),
          portalApi.getInvoices({ limit: 10 }),
          portalApi.getWorkOrders({ limit: 5 }),
        ]);

        setRecentServices(servicesRes.items);
        setUnpaidInvoices(invoicesRes.items.filter(inv => inv.status !== 'paid' && inv.status !== 'void' && inv.amountDue > 0));
        setUpcomingWorkOrders(workOrdersRes.items);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(i18n.resolvedLanguage === 'es' ? 'es-MX' : 'en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency?: { currencyCode?: string; locale?: string }) => {
    return new Intl.NumberFormat(currency?.locale || 'en-US', {
      style: 'currency',
      currency: currency?.currencyCode || 'USD',
    }).format(amount);
  };

  const totalDue = unpaidInvoices.reduce((sum, inv) => sum + inv.amountDue, 0);
  const invoiceCurrency = unpaidInvoices[0];

  return (
    <Box>
      {/* Welcome Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {t('Welcome, {{name}}', { name: customer?.name })}
        </Typography>
        <Typography color="text.secondary">
          {t('Your {{businessName}} customer portal', { businessName: account?.businessName })}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Quick Stats */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 4,
        }}
      >
        {/* Outstanding Balance */}
        <Card sx={dashboardStatCardSx}>
          <CardActionArea onClick={() => navigate('/invoices')} sx={dashboardActionAreaSx}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ReceiptIcon sx={{ color: totalDue > 0 ? 'warning.main' : 'success.main', mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('Balance Due')}
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {loading ? <Skeleton width={80} /> : formatCurrency(totalDue, invoiceCurrency)}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>

        {/* Recent Services */}
        <Card sx={dashboardStatCardSx}>
          <CardActionArea onClick={() => navigate('/service-history')} sx={dashboardActionAreaSx}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <HistoryIcon sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('Last Service')}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {loading ? (
                  <Skeleton width={100} />
                ) : recentServices[0] ? (
                  formatDate(recentServices[0].serviceDate)
                ) : (
                  t('No services yet')
                )}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>

        {/* Upcoming Work */}
        <Card sx={dashboardStatCardSx}>
          <CardActionArea onClick={() => navigate('/work-orders')} sx={dashboardActionAreaSx}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BuildIcon sx={{ color: 'info.main', mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('Scheduled Work')}
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {loading ? <Skeleton width={40} /> : upcomingWorkOrders.length}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>

        {/* Properties */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PoolIcon sx={{ color: 'secondary.main', mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {t('Properties')}
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {customer?.properties?.length || 0}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Recent Service History */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('Recent Service History')}</Typography>
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/service-history')}
            >
              {t('View All')}
            </Typography>
          </Box>

          {loading ? (
            <Box>
              {[1, 2, 3].map((i) => (
                <Box key={i} sx={{ py: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Skeleton width="40%" height={24} />
                  <Skeleton width="60%" height={20} />
                </Box>
              ))}
            </Box>
          ) : recentServices.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {t('No service history yet')}
            </Typography>
          ) : (
            recentServices.map((service, index) => (
              <Box
                key={service.id}
                role="link"
                tabIndex={0}
                sx={{
                  ...dashboardListRowSx,
                  py: 2,
                  borderBottom: index < recentServices.length - 1 ? 1 : 0,
                  borderColor: 'divider',
                }}
                onClick={() => navigate(`/service-history/${service.id}`)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(`/service-history/${service.id}`);
                  }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {formatDate(service.serviceDate)}
                  </Typography>
                  <Chip
                    label={t(formatStatus(service.status))}
                    size="small"
                    color={service.status === 'completed' ? 'success' : 'default'}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {service.property.address}
                  {service.technicianName && ` • ${service.technicianName}`}
                </Typography>
              </Box>
            ))
          )}
        </CardContent>
      </Card>

      {/* Unpaid Invoices */}
      {unpaidInvoices.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{t('Outstanding Invoices')}</Typography>
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate('/invoices')}
              >
                {t('View All')}
              </Typography>
            </Box>

            {unpaidInvoices.slice(0, 3).map((invoice, index) => (
              <Box
                key={invoice.id}
                role="link"
                tabIndex={0}
                sx={{
                  ...dashboardListRowSx,
                  py: 2,
                  borderBottom: index < Math.min(unpaidInvoices.length, 3) - 1 ? 1 : 0,
                  borderColor: 'divider',
                }}
                onClick={() => navigate(`/invoices/${invoice.id}`)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(`/invoices/${invoice.id}`);
                  }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {t('Invoice')} #{invoice.invoiceNumber}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'warning.main' }}>
                    {formatCurrency(invoice.amountDue, invoice)}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('Due Date')}: {invoice.dueDate ? formatDate(invoice.dueDate) : t('Upon receipt')}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
